import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Check,
  CheckCheck,
  Download,
  FileText,
  MessageSquare,
  Paperclip,
  Pencil,
  Search,
  Send,
  Smile,
  X,
} from "lucide-react";

import { api, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import Loader from "@/components/ui/loader";
import { NivaAvatar } from "@/components/NivaAvatar";
import { ChatSenderLabel } from "@/components/ChatSenderLabel";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { timeAgo } from "@/lib/notifications";
import { cn } from "@/lib/utils";

/**
 * The buyer side of listing-interest conversations.
 *
 * Until now these threads only had a staff-facing inbox — a buyer could raise
 * an interest and never see the advisor's reply anywhere in the app. This is
 * that missing inbox: every interest is a thread, unread ones float to the top.
 *
 * Note this is the *listing-interest* thread (`/interests/...`), a different
 * conversation from the Coming Soon pipeline thread (`/lead-pipeline/...`)
 * rendered by MessageThreadModal on the same page.
 */

export interface ChatThread {
  interest_id: string;
  listing_id: string;
  listing_title: string | null;
  listing_reference_code: string | null;
  listing_image_url: string | null;
  agent_name: string | null;
  agent_avatar_url: string | null;
  last_message: string | null;
  last_message_at: string | null;
  last_sender_role: string | null;
  message_count: number;
  unread_count: number;
  created_at: string;
}

interface InterestMessage {
  id: string;
  interest_id: string;
  sender_id: string;
  sender_role: string;
  sender_name: string | null;
  sender_avatar_url: string | null;
  body: string;
  is_read_by_buyer: boolean;
  is_read_by_admin: boolean;
  created_at: string;
  edited_at: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_mime: string | null;
  attachment_size: number | null;
}

interface PendingAttachment {
  attachment_url: string;
  attachment_name: string;
  attachment_mime: string;
  attachment_size: number;
}

type ThreadFilter = "all" | "unread";
type ThreadSort = "recent" | "unread" | "name";

/** Refresh cadence for an open conversation. */
const THREAD_POLL_MS = 20_000;
/** Refresh cadence for the thread list, so new replies surface without a reload. */
const INBOX_POLL_MS = 60_000;

/** Window event carrying the inbox-wide unread total (detail: number). */
export const INTEREST_UNREAD_EVENT = "interest-unread-changed";

/** Enough to react without dragging in an emoji-picker dependency. */
const QUICK_EMOJI = ["👍", "🙏", "😀", "😊", "🎉", "🔥", "✅", "❓", "💰", "📈", "👀", "❤️"];

function isImage(mime: string | null): boolean {
  return !!mime && mime.startsWith("image/");
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Clock time inside a bubble — chats read better than a relative age here. */
function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

function ThreadAvatar({ t, className }: { t: ChatThread; className?: string }) {
  const src = t.agent_avatar_url || t.listing_image_url;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary",
        className
      )}
    >
      {src ? (
        <img src={assetUrl(src) ?? undefined} alt="" className="h-full w-full object-cover" />
      ) : (
        <Building2 className="h-4 w-4" />
      )}
    </span>
  );
}

/** Advisor/staff profile photo beside their messages; initials when they have none. */
function SenderAvatar({ name, src, className }: { name: string; src: string | null; className?: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-[0.55rem] font-bold text-primary",
        className
      )}
    >
      {src ? (
        <img src={assetUrl(src) ?? undefined} alt="" className="h-full w-full object-cover" />
      ) : (
        initials
      )}
    </span>
  );
}

/** Image preview or a download card, depending on the file type. */
function Attachment({ m, mine }: { m: InterestMessage; mine: boolean }) {
  if (!m.attachment_url) return null;
  const href = assetUrl(m.attachment_url) ?? m.attachment_url;

  if (isImage(m.attachment_mime)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="mt-1 block">
        <img
          src={href}
          alt={m.attachment_name ?? "Attachment"}
          className="max-h-64 w-auto max-w-full rounded-xl border border-border/60 object-cover"
        />
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "mt-1 flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-colors",
        mine
          ? "border-primary-foreground/25 bg-primary-foreground/10 hover:bg-primary-foreground/20"
          : "border-border/60 bg-background hover:bg-accent/60"
      )}
    >
      <FileText className="h-5 w-5 shrink-0 opacity-80" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold">{m.attachment_name}</span>
        <span className="block text-[0.65rem] opacity-70">{formatBytes(m.attachment_size)}</span>
      </span>
      <Download className="h-4 w-4 shrink-0 opacity-70" />
    </a>
  );
}

export default function BuyerChats({
  activeId,
  onSelect,
  onUnreadChange,
}: {
  activeId: string | null;
  onSelect: (interestId: string | null) => void;
  /** Reports the inbox-wide unread total so the parent tab can badge it. */
  onUnreadChange?: (total: number) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();

  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ThreadFilter>("all");
  const [sort, setSort] = useState<ThreadSort>("recent");

  const [messages, setMessages] = useState<InterestMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [pending, setPending] = useState<PendingAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const active = threads.find((t) => t.interest_id === activeId) ?? null;

  // The open thread is read as it polls, so a list refresh must not re-badge it.
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  const loadThreads = useCallback(async () => {
    try {
      const rows = await api.get<ChatThread[]>("/interests/mine/threads");
      setThreads(
        rows.map((t) => (t.interest_id === activeIdRef.current ? { ...t, unread_count: 0 } : t))
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your chats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
    const id = window.setInterval(loadThreads, INBOX_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadThreads]);

  const loadMessages = useCallback(async (interestId: string, showSpinner: boolean) => {
    if (showSpinner) setMsgLoading(true);
    try {
      setMessages(await api.get<InterestMessage[]>(`/interests/${interestId}/messages`));
    } catch (e) {
      if (showSpinner) toast.error(e instanceof Error ? e.message : "Could not open this chat");
    } finally {
      if (showSpinner) setMsgLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening a thread marks it read server-side, so clear the badge locally too
  // instead of waiting for the next list refresh.
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    setPending(null);
    setEditingId(null);
    setThreads((prev) =>
      prev.map((t) => (t.interest_id === activeId ? { ...t, unread_count: 0 } : t))
    );
    loadMessages(activeId, true);
    const id = window.setInterval(() => loadMessages(activeId, false), THREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (loading) return;
    const total = threads.reduce((sum, t) => sum + t.unread_count, 0);
    onUnreadChange?.(total);
    window.dispatchEvent(new CustomEvent(INTEREST_UNREAD_EVENT, { detail: total }));
  }, [threads, loading, onUnreadChange]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const visibleThreads = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = threads.filter((t) => {
      if (filter === "unread" && t.unread_count === 0) return false;
      if (!q) return true;
      return (
        (t.listing_title ?? "").toLowerCase().includes(q) ||
        (t.agent_name ?? "").toLowerCase().includes(q) ||
        (t.listing_reference_code ?? "").toLowerCase().includes(q) ||
        (t.last_message ?? "").toLowerCase().includes(q)
      );
    });
    const at = (t: ChatThread) => new Date(t.last_message_at || t.created_at).getTime() || 0;
    return [...rows].sort((a, b) => {
      if (sort === "name") return (a.listing_title ?? "").localeCompare(b.listing_title ?? "");
      if (sort === "unread" && b.unread_count !== a.unread_count) {
        return b.unread_count - a.unread_count;
      }
      return at(b) - at(a);
    });
  }, [threads, query, filter, sort]);

  const uploadFile = async (file: File) => {
    if (!activeId || uploading) return;
    setUploading(true);
    try {
      setPending(
        await api.upload<PendingAttachment>(`/interests/${activeId}/messages/upload`, file)
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const trimmed = body.trim();
    if ((!trimmed && !pending) || !activeId || sending) return;
    setSending(true);
    try {
      const msg = await api.post<InterestMessage>(`/interests/${activeId}/messages`, {
        body: trimmed,
        ...(pending ?? {}),
      });
      setMessages((prev) => [...prev, msg]);
      setBody("");
      setPending(null);
      // Keep the list preview in step without re-fetching the whole inbox.
      setThreads((prev) =>
        prev.map((t) =>
          t.interest_id === activeId
            ? {
                ...t,
                last_message: msg.body || msg.attachment_name || "Attachment",
                last_message_at: msg.created_at,
                last_sender_role: "buyer",
                message_count: t.message_count + 1,
              }
            : t
        )
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const startEdit = (m: InterestMessage) => {
    setEditingId(m.id);
    setEditBody(m.body);
  };

  const saveEdit = async (m: InterestMessage) => {
    const next = editBody.trim();
    if (savingEdit || !activeId) return;
    if (!next && !m.attachment_url) return;
    if (next === m.body) {
      setEditingId(null);
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await api.patch<InterestMessage>(
        `/interests/${activeId}/messages/${m.id}`,
        { body: next }
      );
      setMessages((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      setEditingId(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to edit");
    } finally {
      setSavingEdit(false);
    }
  };

  // The last message the buyer sent — the only one that carries a read receipt,
  // the way Messenger shows a single "Seen" marker at the bottom of the thread.
  const lastOwnId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].sender_id === user?.id) return messages[i].id;
    }
    return null;
  }, [messages, user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-border/80 bg-card px-6 py-16 text-center">
        <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground/50" />
        <p className="mt-3 text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="rounded-2xl border border-border/80 bg-card px-6 py-16 text-center">
        <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground/50" />
        <p className="mt-3 text-sm font-semibold text-foreground">No conversations yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Express interest in a listing and your advisor&apos;s replies will land here.
        </p>
      </div>
    );
  }

  let lastDay = "";

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
      {/* ── Thread list ── */}
      <div
        className={cn(
          "overflow-hidden rounded-2xl border border-border/80 bg-card",
          // On mobile the open conversation takes over the screen entirely.
          activeId && "hidden lg:block"
        )}
      >
        <div className="space-y-2.5 border-b border-border/60 px-3 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/70" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations…"
              className="w-full rounded-lg border border-border/80 bg-background py-2 pl-8 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-primary/60"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border/80 p-0.5">
              {(["all", "unread"] as ThreadFilter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                    filter === f
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as ThreadSort)}
              aria-label="Sort conversations"
              className="ml-auto rounded-lg border border-border/80 bg-background px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary/60"
            >
              <option value="recent">Most recent</option>
              <option value="unread">Unread first</option>
              <option value="name">Name (A–Z)</option>
            </select>
          </div>
        </div>

        <div className="max-h-[34rem] overflow-y-auto">
          {visibleThreads.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No conversations match that.
            </p>
          ) : (
            visibleThreads.map((t) => (
              <button
                key={t.interest_id}
                type="button"
                onClick={() => onSelect(t.interest_id)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors hover:bg-accent/60",
                  t.interest_id === activeId && "bg-primary/5",
                  t.unread_count > 0 && "bg-primary/[0.07]"
                )}
              >
                <ThreadAvatar t={t} className="mt-0.5 h-9 w-9" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-foreground">
                      {t.listing_title || "Untitled listing"}
                    </span>
                    {t.unread_count > 0 && (
                      <span className="ml-auto inline-flex min-w-[1.1rem] shrink-0 items-center justify-center rounded-full bg-primary px-1 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">
                        {t.unread_count}
                      </span>
                    )}
                  </span>
                  {t.message_count > 0 && (
                    <span className="mt-0.5 line-clamp-1 block text-xs text-muted-foreground">
                      {t.last_sender_role === "buyer" && "You: "}
                      {t.last_message?.trim() || "Attachment"}
                    </span>
                  )}
                  <span className="mt-1 block text-[0.65rem] uppercase tracking-wide text-muted-foreground/70">
                    {t.agent_name ? `${t.agent_name} · ` : ""}
                    {timeAgo(t.last_message_at || t.created_at)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Conversation ── */}
      <div
        onDragOver={(e) => {
          if (!activeId) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!activeId) return;
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) uploadFile(file);
        }}
        className={cn(
          "relative flex min-h-[34rem] flex-col overflow-hidden rounded-2xl border bg-card transition-colors",
          dragOver ? "border-dashed border-primary" : "border-border/80",
          !activeId && "hidden lg:flex"
        )}
      >
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-primary/5">
            <p className="rounded-xl bg-card px-4 py-2 text-sm font-semibold text-primary shadow">
              Drop to attach
            </p>
          </div>
        )}

        {!active ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Pick a conversation to read it.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
              <button
                type="button"
                onClick={() => onSelect(null)}
                aria-label="Back to conversations"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border/80 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <ThreadAvatar t={active} className="h-9 w-9" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {active.listing_title || "Untitled listing"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {active.agent_name ? `Advisor: ${active.agent_name}` : "Awaiting advisor assignment"}
                  {active.listing_reference_code ? ` · ${active.listing_reference_code}` : ""}
                </p>
              </div>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
              {msgLoading && messages.length === 0 ? (
                <div className="flex justify-center py-10">
                  <Loader />
                </div>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  const day = dayLabel(m.created_at);
                  const showDay = day !== lastDay;
                  lastDay = day;
                  const editing = editingId === m.id;

                  return (
                    <div key={m.id}>
                      {showDay && (
                        <div className="my-3 flex items-center gap-3">
                          <span className="h-px flex-1 bg-border/60" />
                          <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground/70">
                            {day}
                          </span>
                          <span className="h-px flex-1 bg-border/60" />
                        </div>
                      )}

                      <div className={cn("group flex", mine ? "justify-end" : "justify-start")}>
                        <div className="max-w-[80%]">
                          {!mine && m.sender_role === "niva" ? (
                            <p className="mb-1 flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground/80">
                              <NivaAvatar className="h-6 w-6" />
                              <span className="text-primary">Niva</span> · AI Deal Assistant
                            </p>
                          ) : !mine ? (
                            <p className="mb-1 flex items-center gap-1.5">
                              <SenderAvatar name={m.sender_name ?? ""} src={m.sender_avatar_url} className="h-6 w-6" />
                              <ChatSenderLabel role={m.sender_role} name={m.sender_name} className="text-[0.7rem]" />
                            </p>
                          ) : (
                            <p className="mb-1 text-right">
                              <ChatSenderLabel role={m.sender_role} name={m.sender_name} className="text-[0.7rem]" />
                            </p>
                          )}

                          {editing ? (
                            <div className="rounded-2xl border border-primary/50 bg-background p-2">
                              <AutoGrowTextarea
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    saveEdit(m);
                                  }
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                                rows={2}
                                autoFocus
                                className="w-full bg-transparent text-sm text-foreground outline-none"
                              />
                              <div className="mt-1 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(m)}
                                  disabled={savingEdit}
                                  className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                                >
                                  {savingEdit ? "Saving…" : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-end gap-1.5">
                              {mine && (
                                <button
                                  type="button"
                                  onClick={() => startEdit(m)}
                                  aria-label="Edit message"
                                  title="Edit"
                                  className="mb-1 shrink-0 rounded-md p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <div
                                className={cn(
                                  "rounded-2xl px-3.5 py-2.5 text-sm",
                                  mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                                )}
                              >
                                {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                                <Attachment m={m} mine={mine} />
                              </div>
                            </div>
                          )}

                          <p
                            className={cn(
                              "mt-1 flex items-center gap-1 text-[0.65rem] text-muted-foreground/70",
                              mine && "justify-end"
                            )}
                          >
                            {clockTime(m.created_at)}
                            {m.edited_at ? " · edited" : ""}
                            {mine && m.id === lastOwnId && (
                              <span className="ml-0.5 inline-flex items-center gap-0.5">
                                {m.is_read_by_admin ? (
                                  <>
                                    <CheckCheck className="h-3 w-3 text-primary" />
                                    Seen
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-3 w-3" />
                                    Sent
                                  </>
                                )}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* ── Composer ── */}
            <div className="border-t border-border/60 p-3">
              {pending && (
                <div className="mb-2 flex items-center gap-2.5 rounded-xl border border-border/70 bg-muted/50 px-3 py-2">
                  {isImage(pending.attachment_mime) ? (
                    <img src={pending.attachment_url} alt="" className="h-10 w-10 rounded-lg object-cover" />
                  ) : (
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold text-foreground">
                      {pending.attachment_name}
                    </span>
                    <span className="block text-[0.65rem] text-muted-foreground">
                      {formatBytes(pending.attachment_size)}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setPending(null)}
                    aria-label="Remove attachment"
                    className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {emojiOpen && (
                <div className="mb-2 flex flex-wrap gap-1 rounded-xl border border-border/70 bg-muted/40 p-2">
                  {QUICK_EMOJI.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => {
                        setBody((b) => b + e);
                        setEmojiOpen(false);
                      }}
                      className="rounded-md px-1.5 py-1 text-lg hover:bg-accent"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadFile(file);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  aria-label="Attach a file"
                  title="Attach a file"
                  className="mb-0.5 shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setEmojiOpen((v) => !v)}
                  aria-label="Insert an emoji"
                  title="Emoji"
                  className="mb-0.5 shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Smile className="h-4 w-4" />
                </button>
                <AutoGrowTextarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onPaste={(e) => {
                    // Screenshots pasted straight into the box upload as files.
                    const file = e.clipboardData.files?.[0];
                    if (file) {
                      e.preventDefault();
                      uploadFile(file);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Enter sends; Shift+Enter is a newline.
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder={uploading ? "Uploading…" : "Write a message…"}
                  className="min-h-[2.5rem] flex-1 rounded-xl border border-border/80 bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-primary/60"
                />
                <Button
                  onClick={send}
                  disabled={sending || uploading || (!body.trim() && !pending)}
                  className="shrink-0"
                >
                  <Send className="h-4 w-4" />
                  <span className="ml-1.5 hidden sm:inline">{sending ? "Sending…" : "Send"}</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
